// Analytics layer: GA4 (behaviour, traffic, paths, funnels) + Microsoft Clarity (heatmaps, recordings).
// Everything is a no-op until the IDs are set in frontend/.env and the visitor grants consent.
const GA4_ID = process.env.REACT_APP_GA4_ID || '';
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || '';
const GTM_ID = process.env.REACT_APP_GTM_ID || '';
const CONSENT_KEY = 'biq_consent';
const ATTR_KEY = 'biq_attribution';

const isBrowser = () => typeof window !== 'undefined';
export const analyticsConfigured = () => Boolean(GA4_ID || CLARITY_ID || GTM_ID);
export const analyticsIds = () => ({ ga4: GA4_ID, clarity: CLARITY_ID, gtm: GTM_ID });

const gtag = (...args) => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

let ga4Loaded = false;
let clarityLoaded = false;
let gtmLoaded = false;

const loadScript = (src, attrs = {}) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const loadGa4 = () => {
  if (ga4Loaded || !GA4_ID) return;
  ga4Loaded = true;
  window.gtag = window.gtag || function () { gtag(...arguments); };
  gtag('js', new Date());
  // We fire page_view ourselves on every route change (SPA-safe paths, exits, time on page)
  gtag('config', GA4_ID, { send_page_view: false, anonymize_ip: true });
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
};

// Google Tag Manager container (marketing-managed tags). Fires gtm.js like the official snippet.
const loadGtm = () => {
  if (gtmLoaded || !GTM_ID) return;
  gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
};

const loadClarity = () => {
  if (clarityLoaded || !CLARITY_ID) return;
  clarityLoaded = true;
  window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
  loadScript(`https://www.clarity.ms/tag/${CLARITY_ID}`);
};

export const getConsent = () => (isBrowser() ? localStorage.getItem(CONSENT_KEY) : null); // 'granted' | 'denied' | null

// Consent Mode v2 defaults: denied until the visitor decides. Call once at app start.
export const initAnalytics = () => {
  if (!isBrowser() || !analyticsConfigured()) return;
  captureAttribution();
  const consent = getConsent();
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: consent === 'granted' ? 'granted' : 'denied',
    wait_for_update: 500,
  });
  if (consent === 'granted') {
    loadGtm();
    loadGa4();
    loadClarity();
  }
};

export const grantConsent = () => {
  if (!isBrowser()) return;
  localStorage.setItem(CONSENT_KEY, 'granted');
  gtag('consent', 'update', { analytics_storage: 'granted' });
  loadGtm();
  loadGa4();
  loadClarity();
  if (window.clarity) window.clarity('consent');
};

export const denyConsent = () => {
  if (!isBrowser()) return;
  localStorage.setItem(CONSENT_KEY, 'denied');
  gtag('consent', 'update', { analytics_storage: 'denied' });
};

export const pageTypeFor = (path) => {
  if (path === '/') return 'home';
  if (path.startsWith('/blog/')) return 'blog_post';
  if (path === '/blog') return 'blog_index';
  if (path === '/pricing') return 'pricing';
  if (path === '/contact') return 'contact';
  if (['/briefingiq', '/eventiq', '/platform'].includes(path)) return 'product';
  if (path === '/company') return 'company';
  return 'other';
};

export const trackPageView = (path, { initial = false, ...extra } = {}) => {
  if (!isBrowser() || path.startsWith('/admin')) return;
  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
    page_type: pageTypeFor(path),
    ...extra,
  });
  // GTM: full page loads are covered by the container's built-in Page View; only in-page (soft)
  // navigations need the virtual_page_view Custom Event trigger.
  if (initial) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'virtual_page_view', page_path: path, page_location: window.location.href, page_title: document.title, page_type: pageTypeFor(path), ...extra });
  if (window.clarity) window.clarity('set', 'page_type', pageTypeFor(path));
};

export const track = (name, params = {}) => {
  if (!isBrowser()) return;
  gtag('event', name, { page_path: window.location.pathname, ...params });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, page_path: window.location.pathname, ...params });
  if (window.clarity && params.label) window.clarity('set', name, String(params.label));
};

// First-touch attribution: UTM params, referrer and landing page, kept for the visit and sent with every lead.
export const captureAttribution = () => {
  if (!isBrowser()) return;
  try {
    if (localStorage.getItem(ATTR_KEY)) return;
    const q = new URLSearchParams(window.location.search);
    const ref = document.referrer && !document.referrer.startsWith(window.location.origin) ? document.referrer : '';
    const attr = {
      utm_source: q.get('utm_source') || '',
      utm_medium: q.get('utm_medium') || '',
      utm_campaign: q.get('utm_campaign') || '',
      utm_term: q.get('utm_term') || '',
      utm_content: q.get('utm_content') || '',
      gclid: q.get('gclid') || '',
      referrer: ref,
      landing_page: window.location.pathname,
      first_seen: new Date().toISOString(),
    };
    if (!attr.utm_source && ref) {
      try {
        const host = new URL(ref).hostname.replace(/^www\./, '');
        attr.utm_source = host;
        attr.utm_medium = /google|bing|duckduckgo|yahoo|baidu|ecosia/.test(host) ? 'organic' : /linkedin|twitter|x\.com|facebook|t\.co/.test(host) ? 'social' : 'referral';
      } catch (e) { /* ignore */ }
    }
    if (!attr.utm_source && !ref) {
      attr.utm_source = 'direct';
      attr.utm_medium = 'none';
    }
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  } catch (e) { /* storage blocked */ }
};

export const getAttribution = () => {
  if (!isBrowser()) return {};
  try {
    const attr = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}');
    return { ...attr, submitted_from: window.location.pathname };
  } catch (e) {
    return {};
  }
};
